import React, { useState } from 'react';
import { runInference } from './api';
import InferenceProgress from './InferenceProgress';
import './InferenceProgress.css';
import './App.css';

function App() {
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Handle file selection
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setPreview(URL.createObjectURL(file));
      setResult(null);
      setError(null);
    }
  };

  // Load a sample image from the public folder
  const handleLoadSample = async () => {
    try {
      const response = await fetch('/normal-pelvic-radiograph-female.jpg');
      const blob = await response.blob();
      const file = new File([blob], 'normal-pelvic-radiograph-female.jpg', { type: blob.type });
      setSelectedFile(file);
      setPreview(URL.createObjectURL(blob));
      setResult(null);
      setError(null);
    } catch (err) {
      setError('Could not load sample image.');
    }
  };

  // Handle upload and prediction
  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select an image first');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);
    setProgress(null);

    try {
      const data = await runInference(selectedFile, setProgress);
      setResult(data);
    } catch (err) {
      setError(err.message || 'An error occurred during prediction');
    } finally {
      setLoading(false);
      setProgress(null);
    }
  };

  // Download results as JSON
  const downloadResults = () => {
    const dataStr = JSON.stringify(result, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'landmarks.json';
    link.click();
  };

  // Convert landmarks dict to sorted array for table display
  const landmarksArray = result
    ? Object.entries(result.landmarks)
        .filter(([, coords]) => coords !== null)
        .sort(([a], [b]) => a.localeCompare(b))
    : [];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold text-gray-900">
            MedLandmark AI
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Automated Anatomical Landmark Detection
          </p>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">

          {/* Demo Video */}
          <div className="bg-white shadow rounded-lg p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">Demo Video</h2>
            <div className="aspect-w-16 aspect-h-9">
              <iframe
                width="100%"
                height="480"
                src="https://www.youtube.com/embed/VB2Ge0WrSfY"
                title="MedLandmark Demo"
                frameBorder="0"
                allowFullScreen
              />
            </div>
          </div>

          {/* Upload Section */}
          <div className="bg-white shadow rounded-lg p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">Upload Radiograph</h2>

            <div className="space-y-4">
              {/* File Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Image
                </label>
                <input
                  type="file"
                  accept="image/*,.dcm,.dicom"
                  onChange={handleFileSelect}
                  disabled={loading}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Supported: PNG, JPEG, DICOM (.dcm)
                </p>
              </div>

              {/* Sample Image Loader */}
              <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={handleLoadSample}
                  disabled={loading}
                  className={`px-4 py-2 rounded-md text-sm font-medium border ${
                    loading
                      ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Load a Sample Radiograph
                </button>
                <p className="text-xs text-gray-500">
                  Case courtesy of Ian Bickle,{' '}
                  <a
                    href="https://radiopaedia.org/?lang=us"
                    target="_blank"
                    rel="noreferrer"
                    className="underline hover:text-gray-700"
                  >
                    Radiopaedia.org
                  </a>
                  . From the case{' '}
                  <a
                    href="https://radiopaedia.org/cases/46419?lang=us"
                    target="_blank"
                    rel="noreferrer"
                    className="underline hover:text-gray-700"
                  >
                    rID: 46419
                  </a>
                  .
                </p>
              </div>

              {/* Image Preview */}
              {preview && !loading && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Preview:</p>
                  <img
                    src={preview}
                    alt="Preview"
                    className="max-w-md rounded-lg shadow-md"
                  />
                </div>
              )}

              {/* Upload Button */}
              <button
                onClick={handleUpload}
                disabled={!selectedFile || loading}
                className={`w-full sm:w-auto px-6 py-3 rounded-md font-medium ${
                  loading || !selectedFile
                    ? 'bg-gray-300 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
              >
                {loading ? 'Processing...' : 'Detect Landmarks'}
              </button>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mt-4 p-4 bg-red-50 rounded-md">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}
          </div>

          {/* Progress Panel — shown while inference is running */}
          {loading && (
            <div className="mb-6">
              <InferenceProgress progress={progress} />
            </div>
          )}

          {/* Results Section */}
          {result && (
            <div className="space-y-6">

              {/* Statistics */}
              <div className="bg-white shadow rounded-lg p-6">
                <h2 className="text-xl font-semibold mb-4">Results</h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">Landmarks Detected</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {result.statistics.total_detected}
                    </p>
                  </div>

                  <div className="bg-purple-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">Inference Time</p>
                    <p className="text-2xl font-bold text-purple-600">
                      {result.metadata.inference_time_seconds}s
                    </p>
                  </div>
                </div>

                {/* Metadata */}
                <div className="text-sm text-gray-600 space-y-1">
                  <p>File: {result.metadata.filename}</p>
                  <p>Processed: {new Date(result.metadata.timestamp).toLocaleString()}</p>
                </div>
              </div>

              {/* Annotated Image */}
              <div className="bg-white shadow rounded-lg p-6">
                <h2 className="text-xl font-semibold mb-4">Annotated Image</h2>
                <img
                  src={`data:image/png;base64,${result.annotated_image_base64}`}
                  alt="Annotated"
                  className="max-w-full rounded-lg shadow-md"
                />
              </div>

              {/* Landmarks Table */}
              <div className="bg-white shadow rounded-lg p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold">
                    Detected Landmarks ({landmarksArray.length})
                  </h2>
                  <button
                    onClick={downloadResults}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                  >
                    Download JSON
                  </button>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Label
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          X
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Y
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {landmarksArray.map(([label, coords]) => (
                        <tr key={label}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {label}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {coords[0]}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {coords[1]}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

            </div>
          )}

        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t mt-12">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <p className="text-center text-gray-500 text-sm">
            MedLandmark AI - Powered by SAM + YOLO11
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;
